import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, addDays, addMonths, differenceInCalendarMonths, differenceInCalendarDays } from 'date-fns';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, getDoc, collection, addDoc, getDocs, deleteDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { SPACES } from '../data/spaces';
import { sendPasswordResetEmail, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../services/firebase';
import { getAllBookings, updatePaymentSchedule, getAllMemberCredits, topUpMemberCredits, getAllUsers, updateUserWifiProfile, updateUserProfile, updateUserRole, createBooking, addCreditNoteRef, cancelBooking, markRefunded } from '../services/bookingService';
import { sendBookingConfirmation, sendPaymentLink, sendRefundConfirmation, sendAccessCodeNotification, generateInvoice, generateCreditNote } from '../services/emailService';
import { getAccessFeatureEnabled, setAccessFeatureEnabled, getOrCreateAccessPin } from '../services/accessService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Saves/loads space config from Firestore (with localStorage fallback for demo)
const loadSpaceConfig = async (defaults) => {
  try {
    const snap = await getDoc(doc(db, 'config', 'spaces'));
    if (snap.exists()) return snap.data().spaces;
  } catch (err) {
    console.error('[Firestore] loadSpaceConfig failed:', err);
  }
  return defaults;
};

const saveSpaceConfig = async (spaces) => {
  try {
    await setDoc(doc(db, 'config', 'spaces'), { spaces });
  } catch (err) {
    console.error('[Firestore] saveSpaceConfig failed:', err);
    throw err;
  }
};

const loadManualBlocks = async () => {
  try {
    const snap = await getDocs(collection(db, 'manualBlocks'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[Firestore] loadManualBlocks failed:', err);
  }
  try { return JSON.parse(localStorage.getItem('hub_manual_blocks')) ?? []; } catch { return []; }
};

const saveManualBlock = async (block) => {
  try {
    const ref = await addDoc(collection(db, 'manualBlocks'), { ...block, createdAt: Timestamp.now() });
    return ref.id;
  } catch (err) {
    console.error('[Firestore] saveManualBlock failed:', err);
    const id = `local-${Date.now()}`;
    const existing = JSON.parse(localStorage.getItem('hub_manual_blocks') ?? '[]');
    localStorage.setItem('hub_manual_blocks', JSON.stringify([...existing, { ...block, id }]));
    return id;
  }
};

const loadBankDetails = async () => {
  try {
    const snap = await getDoc(doc(db, 'config', 'bankDetails'));
    if (snap.exists()) return snap.data();
  } catch (err) {
    console.error('[Firestore] loadBankDetails failed:', err);
  }
  return { accountName: 'The Hub Crookwell', bsb: '', accountNumber: '' };
};

const saveBankDetails = async (details) => {
  await setDoc(doc(db, 'config', 'bankDetails'), details);
};

const normalisePhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.trim().replace(/[\s\-().]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (cleaned.startsWith('0')) return '+61' + cleaned.slice(1);
  return cleaned;
};

const loadWifiProfiles = async () => {
  try {
    const snap = await getDoc(doc(db, 'config', 'wifiProfiles'));
    if (snap.exists()) return { profiles: snap.data().profiles || [], defaultProfileId: snap.data().defaultProfileId || null };
  } catch (err) {
    console.error('[Firestore] loadWifiProfiles failed:', err);
  }
  return { profiles: [], defaultProfileId: null };
};

const saveWifiProfiles = async (profiles, defaultProfileId) => {
  await setDoc(doc(db, 'config', 'wifiProfiles'), { profiles, defaultProfileId: defaultProfileId || null });
};

const DEFAULT_FOOTER = {
  brandDescription: 'A modern co-working space in the heart of Crookwell. Flexible desks, private offices, and meeting rooms for professionals, freelancers, and remote workers.',
  address: 'Crookwell NSW 2583, Australia',
  email: 'hello@thehubcrookwell.com.au',
  phone: '',
  hoursWeekday: 'Mon–Fri 8am–6pm',
  hoursSaturday: 'Sat 9am–1pm',
  hoursMembersNote: 'Members: 24/7 access',
  tagline: 'Built for co-workers, by co-workers.',
};

const loadFooterConfig = async () => {
  try {
    const snap = await getDoc(doc(db, 'config', 'footer'));
    if (snap.exists()) return { ...DEFAULT_FOOTER, ...snap.data() };
  } catch (err) {
    console.error('[Firestore] loadFooterConfig failed:', err);
  }
  return DEFAULT_FOOTER;
};

const saveFooterConfig = async (config) => {
  await setDoc(doc(db, 'config', 'footer'), config);
};

const deleteManualBlock = async (id) => {
  try {
    await deleteDoc(doc(db, 'manualBlocks', id));
  } catch {}
  const existing = JSON.parse(localStorage.getItem('hub_manual_blocks') ?? '[]');
  localStorage.setItem('hub_manual_blocks', JSON.stringify(existing.filter((b) => b.id !== id)));
};

// ─── Admin Booking Form ───────────────────────────────────────────────────────

function AdminBookingForm({ spaces, users, bankDetails, onCreated, onCancel }) {
  const initialSpace = spaces[0];
  const [form, setForm] = useState({
    memberId: '',
    guestFirst: '', guestLast: '', guestEmail: '',
    spaceId: initialSpace?.id || '',
    pricingType: initialSpace?.pricingOptions[0]?.type || '',
    startDate: '', endDate: '',
    seatName: '',
    discountType: 'none', discountValue: '',
    paymentMethod: 'card',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const space = spaces.find((s) => s.id === form.spaceId);
  const isHourly = form.pricingType === 'hourly';
  const isHalfDay = form.pricingType === 'half-day';

  const start = form.startDate ? new Date(form.startDate) : null;
  const end = (() => {
    if (isHalfDay && start) return new Date(start.getTime() + 4 * 3600000);
    return form.endDate ? new Date(form.endDate) : null;
  })();

  const basePrice = (() => {
    if (!space || !start) return 0;
    const opt = space.pricingOptions.find((p) => p.type === form.pricingType);
    if (!opt) return 0;
    if (form.pricingType === 'hourly' && end) return Math.round((end - start) / 3600000) * opt.price;
    if (form.pricingType === 'daily' && end) return differenceInCalendarDays(end, start) * opt.price;
    if (form.pricingType === 'monthly' && end) return differenceInCalendarMonths(end, start) * opt.price;
    return opt.price;
  })();

  const discountAmount = (() => {
    const val = parseFloat(form.discountValue) || 0;
    if (form.discountType === 'flat') return Math.min(val, basePrice);
    if (form.discountType === 'percent') return Math.round(basePrice * val / 100);
    return 0;
  })();
  const finalPrice = Math.max(0, basePrice - discountAmount);

  const selectedMember = users.find((u) => u.id === form.memberId);
  const memberEmail = form.memberId === 'guest' ? form.guestEmail : (selectedMember?.email || '');
  const memberFirst = form.memberId === 'guest' ? form.guestFirst : (selectedMember?.firstName || selectedMember?.email?.split('@')[0] || '');
  const memberLast = form.memberId === 'guest' ? form.guestLast : (selectedMember?.lastName || '');
  const memberName = `${memberFirst} ${memberLast}`.trim() || memberEmail;

  const handleSpaceChange = (spaceId) => {
    const sp = spaces.find((s) => s.id === spaceId);
    setForm((f) => ({ ...f, spaceId, pricingType: sp?.pricingOptions[0]?.type || '', seatName: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!memberEmail) { toast.error('Please select a member or enter a guest email.'); return; }
    if (!start || isNaN(start)) { toast.error('Please enter a start date/time.'); return; }
    if (!isHalfDay && (form.pricingType === 'daily' || form.pricingType === 'monthly' || form.pricingType === 'hourly') && (!end || isNaN(end))) {
      toast.error('Please enter an end date/time.'); return;
    }
    setSubmitting(true);
    try {
      const bookingId = await createBooking({
        userId: (form.memberId && form.memberId !== 'guest') ? form.memberId : null,
        spaceId: form.spaceId,
        pricingType: form.pricingType,
        startDate: start,
        endDate: end || start,
        price: finalPrice,
        originalPrice: basePrice,
        discountAmount,
        seatName: form.seatName || null,
        firstName: memberFirst,
        lastName: memberLast,
        email: memberEmail,
        notes: form.notes || null,
        paymentMethod: form.paymentMethod === 'card' ? 'card' : 'bank_deposit',
        paymentStatus: 'awaiting_payment',
        status: 'confirmed',
        adminCreated: true,
      });

      if (form.paymentMethod === 'bank') {
        await sendBookingConfirmation({
          to: memberEmail, name: memberName, bookingId,
          spaceName: space?.name || '',
          seatName: form.seatName || '',
          startDate: format(start, 'd MMM yyyy'),
          endDate: end ? format(end, 'd MMM yyyy') : '',
          price: finalPrice,
          pricingType: form.pricingType,
          paymentMethod: 'bank_deposit',
          bsb: bankDetails.bsb || '',
          accountNumber: bankDetails.accountNumber || '',
          accountName: bankDetails.accountName || '',
        }).catch(() => {});
      } else {
        const payUrl = `${window.location.origin}/pay?bookingId=${bookingId}`;
        await sendPaymentLink({
          to: memberEmail, name: memberName, bookingId,
          spaceName: space?.name || '',
          startDate: format(start, 'd MMM yyyy'),
          price: finalPrice,
          payUrl,
        }).catch(() => {});
      }

      toast.success(`Booking created. Email sent to ${memberEmail}.`);
      onCreated();
    } catch (err) {
      toast.error(err.message || 'Failed to create booking.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">New Booking on Behalf of Member</h3>
        <button type="button" onClick={onCancel} className="text-sm text-slate-400 hover:text-slate-600">Cancel</button>
      </div>

      {/* Member selection */}
      <div>
        <label className="label">Member</label>
        <select className="input-field" value={form.memberId} onChange={(e) => setForm((f) => ({ ...f, memberId: e.target.value }))}>
          <option value="">— Select member —</option>
          {users.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)).map((u) => (
            <option key={u.id} value={u.id}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} ({u.email})</option>
          ))}
          <option value="guest">Guest / No account</option>
        </select>
      </div>

      {form.memberId === 'guest' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">First Name</label>
            <input type="text" className="input-field" value={form.guestFirst} onChange={(e) => setForm((f) => ({ ...f, guestFirst: e.target.value }))} />
          </div>
          <div>
            <label className="label">Last Name</label>
            <input type="text" className="input-field" value={form.guestLast} onChange={(e) => setForm((f) => ({ ...f, guestLast: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="label">Email</label>
            <input type="email" className="input-field" value={form.guestEmail} onChange={(e) => setForm((f) => ({ ...f, guestEmail: e.target.value }))} required />
          </div>
        </div>
      )}

      {/* Space & pricing */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Space</label>
          <select className="input-field" value={form.spaceId} onChange={(e) => handleSpaceChange(e.target.value)}>
            {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Pricing</label>
          <select className="input-field" value={form.pricingType} onChange={(e) => {
            const pt = e.target.value;
            setForm((f) => ({ ...f, pricingType: pt, paymentMethod: pt === 'monthly' ? 'bank' : f.paymentMethod }));
          }}>
            {space?.pricingOptions.map((opt) => (
              <option key={opt.type} value={opt.type}>{opt.label} — ${opt.price}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Seat */}
      {space?.seats?.length > 0 && (
        <div>
          <label className="label">Seat</label>
          <select className="input-field" value={form.seatName} onChange={(e) => setForm((f) => ({ ...f, seatName: e.target.value }))}>
            <option value="">— Any seat —</option>
            {space.seats.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">{isHourly || isHalfDay ? 'Start (date & time)' : 'Start Date'}</label>
          <input
            type={isHourly || isHalfDay ? 'datetime-local' : 'date'}
            className="input-field"
            value={form.startDate}
            onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
            required
          />
        </div>
        {!isHalfDay && (
          <div>
            <label className="label">{isHourly ? 'End (date & time)' : 'End Date'}</label>
            <input
              type={isHourly ? 'datetime-local' : 'date'}
              className="input-field"
              value={form.endDate}
              onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
              required
            />
          </div>
        )}
      </div>

      {/* Discount + price */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="label">Discount</label>
          <select className="input-field" value={form.discountType} onChange={(e) => setForm((f) => ({ ...f, discountType: e.target.value, discountValue: '' }))}>
            <option value="none">No discount</option>
            <option value="flat">Flat ($)</option>
            <option value="percent">Percentage (%)</option>
          </select>
        </div>
        {form.discountType !== 'none' && (
          <div>
            <label className="label">{form.discountType === 'flat' ? 'Amount ($)' : 'Percentage (%)'}</label>
            <input
              type="number" min="0" max={form.discountType === 'percent' ? 100 : undefined}
              step="0.01" className="input-field"
              value={form.discountValue}
              onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
            />
          </div>
        )}
        <div className={form.discountType === 'none' ? 'col-span-2' : ''}>
          <label className="label">Total</label>
          <div className="input-field bg-slate-50 flex items-center gap-2">
            {discountAmount > 0 && <span className="line-through text-slate-400 text-sm">${basePrice}</span>}
            <span className="font-semibold text-slate-800">${finalPrice}</span>
            {discountAmount > 0 && <span className="text-xs text-green-600">-${discountAmount} off</span>}
          </div>
        </div>
      </div>

      {/* Payment method */}
      <div>
        <label className="label">Payment Method</label>
        <div className="flex flex-col sm:flex-row gap-3">
          {[
            form.pricingType !== 'monthly' && { id: 'card', label: 'Credit Card — send member a payment link' },
            { id: 'bank', label: 'Bank Transfer — send member bank details' },
          ].filter(Boolean).map((m) => (
            <label key={m.id} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer text-sm font-medium transition-colors ${form.paymentMethod === m.id ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              <input type="radio" className="hidden" checked={form.paymentMethod === m.id} onChange={() => setForm((f) => ({ ...f, paymentMethod: m.id }))} />
              {m.label}
            </label>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="label">Notes (internal)</label>
        <textarea rows={2} className="input-field" placeholder="Optional — visible to admin only" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
          {submitting ? 'Creating…' : 'Create Booking & Send Email'}
        </button>
      </div>
    </form>
  );
}

// ─── Space Config Editor ──────────────────────────────────────────────────────

const PricingRow = ({ option, onChange, onDelete }) => (
  <div className="space-y-1.5">
    <div className="grid grid-cols-3 gap-2 items-center">
      <span className="text-sm text-slate-600 capitalize">{option.label}</span>
      <div className="flex items-center gap-1">
        <span className="text-slate-400 text-sm">$</span>
        <input
          type="number"
          min="0"
          step="1"
          className="input-field py-1.5 text-sm"
          value={option.price}
          onChange={(e) => onChange({ ...option, price: +e.target.value })}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">/{option.type === 'hourly' ? 'hr' : option.type === 'daily' ? 'day' : option.type === 'half-day' ? 'half-day' : 'month'}</span>
        {onDelete && (
          <button type="button" onClick={onDelete} className="text-xs text-red-400 hover:text-red-600">Remove</button>
        )}
      </div>
    </div>
    {option.type === 'daily' && (
      <div className="grid grid-cols-3 gap-2 items-center pl-0">
        <span className="text-xs text-slate-400">Meeting room credits</span>
        <input
          type="number"
          min="0"
          step="0.5"
          className="input-field py-1.5 text-sm"
          value={option.meetingRoomCredits ?? 0}
          onChange={(e) => onChange({ ...option, meetingRoomCredits: +e.target.value })}
        />
        <span className="text-xs text-slate-400">hrs included</span>
      </div>
    )}
  </div>
);

const SpaceEditor = ({ space, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(space);

  const handleSave = () => {
    onSave(form);
    setEditing(false);
    toast.success(`${form.name} updated.`);
  };

  if (!editing) {
    return (
      <div className="card p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: space.color }} />
            <div>
              <h3 className="font-semibold text-slate-800">{space.name}</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {space.type === 'desk' ? `${space.totalSeats} desk${space.totalSeats !== 1 ? 's' : ''}` : `Capacity: ${space.capacity}`}
                {' · '}
                {space.pricingOptions.map((p) => `$${p.price}/${p.type === 'hourly' ? 'hr' : p.type === 'daily' ? 'day' : 'mo'}`).join(' · ')}
              </p>
            </div>
          </div>
          <button onClick={() => setEditing(true)} className="text-sm text-brand-500 hover:text-brand-700 font-medium">Edit</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-5 ring-2 ring-brand-300 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Editing: {form.name}</h3>
        <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label">Display Name</label>
          <input className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        {form.type !== 'desk' && (
          <div>
            <label className="label">Capacity (people)</label>
            <input
              type="number" min="1" className="input-field"
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: +e.target.value })}
            />
          </div>
        )}
      </div>

      <div>
        <label className="label">Description</label>
        <textarea className="input-field resize-none" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>

      {/* Amenities */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label mb-0">Amenities / What's Included</label>
          <button
            type="button"
            onClick={() => setForm({ ...form, amenities: [...(form.amenities || []), ''] })}
            className="text-xs text-brand-500 hover:text-brand-700 font-medium"
          >+ Add</button>
        </div>
        <div className="space-y-2">
          {(form.amenities || []).map((amenity, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                className="input-field py-1.5 text-sm flex-1"
                value={amenity}
                onChange={(e) => {
                  const amenities = [...form.amenities];
                  amenities[i] = e.target.value;
                  setForm({ ...form, amenities });
                }}
              />
              <button
                type="button"
                onClick={() => setForm({ ...form, amenities: form.amenities.filter((_, idx) => idx !== i) })}
                className="text-red-400 hover:text-red-600 p-1 flex-shrink-0"
                title="Remove"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Max sub-members (team members allowed in the space) */}
      {form.type === 'desk' && (
        <div>
          <label className="label">Max Permitted Sub-members</label>
          <input
            type="number" min="0" max="50" className="input-field"
            value={form.maxSubMembers ?? 0}
            onChange={(e) => setForm({ ...form, maxSubMembers: +e.target.value })}
          />
          <p className="text-xs text-slate-400 mt-1">
            Employees or guests the member can register as authorised visitors. Set to 0 to disable.
          </p>
        </div>
      )}

      {/* Individual desk/seat names for desk-type spaces */}
      {form.type === 'desk' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="label mb-0">Desk Names</label>
            <button
              type="button"
              onClick={() => {
                const seats = [...(form.seats || []), `Desk ${(form.seats?.length || 0) + 1}`];
                setForm({ ...form, seats, totalSeats: seats.length });
              }}
              className="text-xs text-brand-500 hover:text-brand-700 font-medium"
            >
              + Add desk
            </button>
          </div>
          <div className="space-y-2">
            {(form.seats || []).map((seat, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  className="input-field py-1.5 text-sm flex-1"
                  value={seat}
                  onChange={(e) => {
                    const seats = [...form.seats];
                    seats[i] = e.target.value;
                    setForm({ ...form, seats });
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const seats = form.seats.filter((_, idx) => idx !== i);
                    setForm({ ...form, seats, totalSeats: seats.length });
                  }}
                  className="text-red-400 hover:text-red-600 p-1 flex-shrink-0"
                  title="Remove desk"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">{form.seats?.length || 0} desk{form.seats?.length !== 1 ? 's' : ''} total — renaming syncs with manual block dropdown</p>
        </div>
      )}

      <div>
        <label className="label">Pricing</label>
        <div className="space-y-2 mt-1">
          {form.pricingOptions.map((opt, i) => (
            <PricingRow
              key={opt.type}
              option={opt}
              onChange={(updated) => {
                const opts = [...form.pricingOptions];
                opts[i] = updated;
                setForm({ ...form, pricingOptions: opts });
              }}
              onDelete={form.pricingOptions.length > 1 ? () => {
                setForm({ ...form, pricingOptions: form.pricingOptions.filter((_, j) => j !== i) });
              } : null}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={() => setEditing(false)} className="btn-secondary text-sm">Cancel</button>
        <button onClick={handleSave} className="btn-primary text-sm">Save Changes</button>
      </div>
    </div>
  );
};

// ─── Manual Block Form ────────────────────────────────────────────────────────

const ManualBlockForm = ({ spaces, users, onAdd }) => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [form, setForm] = useState({
    spaceIds: [spaces[0]?.id || ''],
    seatName: '',
    startDate: today,
    endDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    note: '',
    memberName: '',
    userEmail: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const toggleSpace = (id) => {
    setForm((f) => {
      const has = f.spaceIds.includes(id);
      const next = has ? f.spaceIds.filter((s) => s !== id) : [...f.spaceIds, id];
      return { ...f, spaceIds: next.length ? next : f.spaceIds, seatName: '' };
    });
  };

  // Only show seat picker if exactly one space with seats is selected
  const singleSpace = form.spaceIds.length === 1 ? spaces.find((s) => s.id === form.spaceIds[0]) : null;
  const hasSeats = singleSpace?.seats?.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.spaceIds.length === 0 || !form.startDate || !form.endDate) { toast.error('Fill in all required fields.'); return; }
    if (form.endDate < form.startDate) { toast.error('End date must be after start date.'); return; }
    if (hasSeats && !form.seatName) { toast.error('Please select a specific desk to block.'); return; }
    setSubmitting(true);
    // Create one block per selected space
    for (const spaceId of form.spaceIds) {
      await onAdd({ ...form, spaceId, seatName: form.spaceIds.length === 1 ? form.seatName : null });
    }
    setForm({ spaceIds: [spaces[0]?.id || ''], seatName: '', startDate: today, endDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'), note: '', memberName: '', userEmail: '' });
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="card p-5 space-y-4">
      <h3 className="font-semibold text-slate-800">Add Manual Block</h3>
      <p className="text-sm text-slate-500">Block out dates for off-system agreements, holidays, or maintenance. Select multiple spaces to block them all at once.</p>

      <div>
        <label className="label">Spaces *</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {spaces.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => toggleSpace(s.id)}
              className={`text-sm px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                form.spaceIds.includes(s.id)
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-gray-200 text-slate-500 hover:border-gray-300'
              }`}
            >
              {s.name}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, spaceIds: spaces.map((s) => s.id), seatName: '' }))}
            className="text-xs text-brand-500 hover:text-brand-700 underline ml-1"
          >
            Select All
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {hasSeats && (
          <div>
            <label className="label">Specific Desk *</label>
            <select className="input-field" value={form.seatName} onChange={(e) => setForm({ ...form, seatName: e.target.value })}>
              <option value="">— Select desk —</option>
              {singleSpace.seats.map((seat) => (
                <option key={seat} value={seat}>{seat}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="label">Member / Reason</label>
          <input
            className="input-field" placeholder="e.g. Christmas Day closure"
            value={form.memberName} onChange={(e) => setForm({ ...form, memberName: e.target.value })}
          />
        </div>
        <div>
          <label className="label">Start Date *</label>
          <input type="date" className="input-field" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
        </div>
        <div>
          <label className="label">End Date *</label>
          <input type="date" className="input-field" min={form.startDate} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
        </div>
      </div>

      <div>
        <label className="label">Linked Member <span className="text-slate-400 font-normal">(optional — shows block on their dashboard)</span></label>
        <select
          className="input-field"
          value={form.userEmail}
          onChange={(e) => setForm({ ...form, userEmail: e.target.value })}
        >
          <option value="">— No linked member —</option>
          {users.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)).map((u) => (
            <option key={u.id} value={u.email}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} ({u.email})</option>
          ))}
        </select>
      </div>

      <div>
        <label className="label">Notes (internal)</label>
        <textarea className="input-field resize-none" rows={2} placeholder="Any notes about this block..." value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
          {submitting ? 'Blocking…' : form.spaceIds.length > 1 ? `Block ${form.spaceIds.length} Spaces` : 'Block Dates'}
        </button>
      </div>
    </form>
  );
};

// ─── Manual Block List ────────────────────────────────────────────────────────

const BlockCard = ({ block, spaces, users, onDelete, accessCodesEnabled }) => {
  const space = spaces.find((s) => s.id === block.spaceId);
  const start = block.startDate?.toDate ? block.startDate.toDate() : new Date(block.startDate);
  const end = block.endDate?.toDate ? block.endDate.toDate() : new Date(block.endDate);
  const [generatingPin, setGeneratingPin] = useState(false);
  const [localPin, setLocalPin] = useState(block.accessPin || null);
  const [localAccessStatus, setLocalAccessStatus] = useState(block.accessCodeStatus || null);
  const [sendingPin, setSendingPin] = useState(false);

  const linkedUser = block.userEmail ? users.find((u) => u.email === block.userEmail) : null;

  const handleGeneratePin = async () => {
    if (!linkedUser) { toast.error('Link a member to this block first.'); return; }
    setGeneratingPin(true);
    try {
      const pin = await getOrCreateAccessPin(linkedUser.id);
      await updateDoc(doc(db, 'manualBlocks', block.id), {
        accessPin: pin,
        accessCodeStatus: 'pending',
        userId: linkedUser.id,
      });
      setLocalPin(pin);
      setLocalAccessStatus('pending');
      toast.success(`Door PIN ${pin} assigned.`);
    } catch (err) {
      toast.error(err.message || 'Failed to generate PIN.');
    }
    setGeneratingPin(false);
  };

  const handleSendPin = async () => {
    if (!localPin || !linkedUser?.email) return;
    setSendingPin(true);
    try {
      await sendAccessCodeNotification({
        to: linkedUser.email,
        name: `${linkedUser.firstName || ''} ${linkedUser.lastName || ''}`.trim() || linkedUser.email,
        phone: linkedUser.phone || '',
        pin: localPin,
        spaceName: space?.name || block.spaceId,
        startDate: format(start, 'EEE d MMM yyyy'),
        endDate: format(end, 'EEE d MMM yyyy'),
        bookingId: block.id,
      });
      toast.success('PIN sent to member.');
    } catch (err) {
      toast.error(err.message || 'Failed to send PIN.');
    }
    setSendingPin(false);
  };

  return (
    <div className="card p-4 flex items-start gap-4">
      <div className="flex-shrink-0 w-1.5 self-stretch rounded-full" style={{ backgroundColor: space?.color || '#94a3b8' }} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-semibold text-slate-700 text-sm">{space?.name || block.spaceId}</span>
          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">Manual Block</span>
          {localAccessStatus && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${{
              pending: 'bg-yellow-100 text-yellow-700',
              provisioned: 'bg-blue-100 text-blue-700',
              active: 'bg-green-100 text-green-700',
              expired: 'bg-gray-100 text-gray-500',
              revoked: 'bg-red-100 text-red-600',
            }[localAccessStatus] || 'bg-gray-100 text-gray-500'}`}>
              {localAccessStatus === 'pending' ? 'Access Pending' :
               localAccessStatus === 'provisioned' ? 'Access Provisioned' :
               localAccessStatus === 'active' ? 'Access Active' :
               localAccessStatus === 'expired' ? 'Access Expired' :
               localAccessStatus === 'revoked' ? 'Access Revoked' : localAccessStatus}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500">
          {format(start, 'd MMM yyyy')} → {format(end, 'd MMM yyyy')}
        </p>
        {block.seatName && <p className="text-xs text-slate-500 mt-0.5 font-medium">{block.seatName}</p>}
        {block.memberName && <p className="text-xs text-slate-400 mt-0.5">{block.memberName}</p>}
        {block.userEmail && <p className="text-xs text-slate-400 mt-0.5">{block.userEmail}</p>}
        {block.note && <p className="text-xs text-slate-400 mt-0.5 italic">"{block.note}"</p>}
        {accessCodesEnabled && linkedUser && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {localPin ? (
              <>
                <span className="text-xs text-slate-400">
                  PIN: <span className="font-mono font-bold tracking-widest text-slate-600">{localPin}</span>
                </span>
                <button
                  onClick={handleSendPin}
                  disabled={sendingPin}
                  className="text-xs text-brand-500 hover:text-brand-700 underline disabled:opacity-50"
                >
                  {sendingPin ? 'Sending…' : 'Send PIN'}
                </button>
              </>
            ) : (
              <button
                onClick={handleGeneratePin}
                disabled={generatingPin}
                className="text-xs text-brand-500 hover:text-brand-700 underline disabled:opacity-50"
              >
                {generatingPin ? 'Generating…' : 'Generate Door Code'}
              </button>
            )}
          </div>
        )}
      </div>
      <button
        onClick={() => onDelete(block.id)}
        className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors p-1"
        title="Remove block"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>
  );
};

const BlockList = ({ blocks, spaces, users, onDelete, accessCodesEnabled }) => {
  if (blocks.length === 0) {
    return (
      <div className="card p-6 text-center text-sm text-slate-400">
        No manual blocks. Add one above to reserve dates for off-system agreements.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {blocks.map((block) => (
        <BlockCard key={block.id} block={block} spaces={spaces} users={users} onDelete={onDelete} accessCodesEnabled={accessCodesEnabled} />
      ))}
    </div>
  );
};

// ─── Payment Row ─────────────────────────────────────────────────────────────

const PaymentRow = ({ booking, payment, onToggle }) => {
  const [mode, setMode] = useState(null); // null | 'marking' | 'editing'
  const [paidDate, setPaidDate] = useState('');
  const [xeroNum, setXeroNum] = useState('');

  const openMarkPaid = () => {
    setPaidDate(format(new Date(), 'yyyy-MM-dd'));
    setXeroNum('');
    setMode('marking');
  };

  const openEdit = () => {
    setPaidDate(payment.paidDate || format(new Date(), 'yyyy-MM-dd'));
    setXeroNum(payment.xeroInvoiceNumber || '');
    setMode('editing');
  };

  const handleConfirm = async () => {
    await onToggle(booking, payment, { paidDate, xeroInvoiceNumber: xeroNum || null });
    setMode(null);
  };

  return (
    <div className="py-2.5 border-t border-gray-100 first:border-t-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-700">{payment.label}</p>
          <p className="text-xs text-slate-400">Due {payment.dueDate}</p>
          {payment.paid && (
            <p className="text-xs text-green-600 mt-0.5">
              {payment.paidDate ? `Paid ${payment.paidDate}` : 'Paid'}
              {payment.xeroInvoiceNumber && <span className="text-slate-400"> · {payment.xeroInvoiceNumber}</span>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-sm font-semibold text-slate-800">${payment.amount}</span>
          {payment.paid ? (
            <>
              <button
                onClick={openEdit}
                className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
              >
                ✓ Paid
              </button>
              <button
                onClick={() => onToggle(booking, payment)}
                className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                title="Mark as unpaid"
              >
                Unpay
              </button>
            </>
          ) : mode === null ? (
            <button
              onClick={openMarkPaid}
              className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 hover:bg-amber-100 hover:text-amber-700 transition-colors"
            >
              Mark Paid
            </button>
          ) : null}
        </div>
      </div>
      {(mode === 'marking' || mode === 'editing') && (
        <div className="mt-2 p-3 bg-gray-50 rounded-xl space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label text-xs">Payment Date</label>
              <input
                type="date"
                className="input-field py-1.5 text-sm"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
              />
            </div>
            <div>
              <label className="label text-xs">Xero Invoice #</label>
              <input
                type="text"
                className="input-field py-1.5 text-sm"
                placeholder="INV-001"
                value={xeroNum}
                onChange={(e) => setXeroNum(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setMode(null)} className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1">Cancel</button>
            <button onClick={handleConfirm} className="btn-primary text-xs px-3 py-1.5">
              {mode === 'editing' ? 'Update' : 'Confirm Paid'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Member Credits ───────────────────────────────────────────────────────────

const MemberCreditCard = ({ member, currentMonth, onTopUp, userRecord }) => {
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpMonth, setTopUpMonth] = useState(currentMonth);
  const [topUpHours, setTopUpHours] = useState(5);
  const [saving, setSaving] = useState(false);

  const monthCredits = member.credits?.[currentMonth] || { total: 0, used: 0 };
  const available = Math.max(0, monthCredits.total - monthCredits.used);

  const handleTopUp = async () => {
    if (topUpHours < 1) return;
    setSaving(true);
    try {
      await onTopUp(member.id, topUpMonth, topUpHours);
      setShowTopUp(false);
    } finally {
      setSaving(false);
    }
  };

  const historyEntries = Object.entries(member.credits || {})
    .filter(([m]) => m !== currentMonth)
    .sort(([a], [b]) => b.localeCompare(a))
    .slice(0, 3);

  const displayName = member.name || userRecord?.displayName || member.email || member.id;
  const initials = (displayName.charAt(0) || '?').toUpperCase();
  const phone = userRecord?.phone;
  const company = userRecord?.company;

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-brand-600">{initials}</span>
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">{displayName}</h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
              {member.email && <p className="text-xs text-slate-400">{member.email}</p>}
              {phone && <p className="text-xs text-slate-400">{phone}</p>}
              {company && <p className="text-xs text-slate-400 italic">{company}</p>}
            </div>
          </div>
        </div>
        <button
          onClick={() => { setShowTopUp(!showTopUp); setTopUpMonth(currentMonth); setTopUpHours(5); }}
          className="text-sm text-brand-500 hover:text-brand-700 font-medium flex-shrink-0 ml-2"
        >
          {showTopUp ? 'Cancel' : 'Top Up'}
        </button>
      </div>

      {/* Current month */}
      <div className="flex items-center gap-4 text-sm mb-1">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide w-16">{currentMonth}</span>
        <div className="flex gap-4 flex-1">
          <span className="text-slate-600">Total: <strong>{monthCredits.total} hrs</strong></span>
          <span className="text-slate-600">Used: <strong className={monthCredits.used > 0 ? 'text-amber-600' : ''}>{monthCredits.used} hrs</strong></span>
          <span>
            Available: <strong className={available > 0 ? 'text-emerald-600' : 'text-red-500'}>{available} hrs</strong>
          </span>
        </div>
      </div>

      {/* Credit bar */}
      {monthCredits.total > 0 && (
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-1.5 mb-3">
          <div
            className="h-full rounded-full bg-emerald-400 transition-all"
            style={{ width: `${Math.min(100, (monthCredits.used / monthCredits.total) * 100)}%` }}
          />
        </div>
      )}

      {/* Top-up form */}
      {showTopUp && (
        <div className="bg-gray-50 rounded-xl p-3 space-y-3 mb-3">
          <p className="text-sm font-semibold text-slate-700">Add credit hours</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label text-xs">Month</label>
              <input
                type="month"
                className="input-field py-1.5 text-sm"
                value={topUpMonth}
                onChange={(e) => setTopUpMonth(e.target.value)}
              />
            </div>
            <div>
              <label className="label text-xs">Hours to Add</label>
              <input
                type="number"
                min="1"
                max="100"
                className="input-field py-1.5 text-sm"
                value={topUpHours}
                onChange={(e) => setTopUpHours(+e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowTopUp(false)} className="btn-secondary text-xs px-3 py-1.5">Cancel</button>
            <button
              onClick={handleTopUp}
              disabled={saving || topUpHours < 1}
              className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
            >
              {saving ? 'Saving…' : `Add ${topUpHours} hr${topUpHours !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* History */}
      {historyEntries.length > 0 && (
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs font-medium text-slate-400 mb-1.5">Previous months</p>
          <div className="space-y-1">
            {historyEntries.map(([month, c]) => {
              const expired = Math.max(0, c.total - c.used);
              return (
                <div key={month} className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="font-medium w-16">{month}</span>
                  <span>{c.used}/{c.total} hrs used</span>
                  {expired > 0 && <span className="text-amber-500">{expired} hr{expired !== 1 ? 's' : ''} expired</span>}
                  {expired === 0 && c.total > 0 && <span className="text-emerald-600">all used</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Booking Card ─────────────────────────────────────────────────────────────

const BookingCard = ({ booking, spaces }) => {
  const space = spaces.find((s) => s.id === booking.spaceId);
  const toD = (v) => v?.toDate ? v.toDate() : (v ? new Date(v) : null);
  const start = toD(booking.startDate);
  const end = toD(booking.endDate);
  const created = toD(booking.createdAt);

  const [showRefund, setShowRefund] = useState(false);
  const [creditNoteRef, setCreditNoteRef] = useState(booking.creditNoteRef || '');
  const [processing, setProcessing] = useState(false);
  const [localRefundStatus, setLocalRefundStatus] = useState(booking.refundStatus || null);
  const [localStatus, setLocalStatus] = useState(booking.status);
  const [sendingPin, setSendingPin] = useState(false);

  const handleSendPin = async () => {
    if (!booking.accessPin) { toast.error('No access PIN on this booking.'); return; }
    if (!booking.email) { toast.error('No email address on this booking.'); return; }
    setSendingPin(true);
    try {
      const startStr = start ? format(start, 'EEE d MMM yyyy, h:mm a') : undefined;
      const endStr = end ? format(end, 'EEE d MMM yyyy, h:mm a') : undefined;
      await sendAccessCodeNotification({
        to: booking.email,
        name: `${booking.firstName || ''} ${booking.lastName || ''}`.trim() || booking.email,
        phone: booking.phone || null,
        pin: booking.accessPin,
        spaceName: space?.name || booking.spaceId,
        startDate: startStr,
        endDate: endStr,
        bookingId: booking.id,
      });
      toast.success('PIN sent to member via email' + (booking.phone ? ' & SMS' : '') + '.');
    } catch (err) {
      toast.error(err.message || 'Failed to send PIN notification.');
    }
    setSendingPin(false);
  };

  const statusColor = {
    confirmed: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-gray-100 text-gray-500',
  }[localStatus] || 'bg-gray-100 text-gray-500';

  const pricingLabel = {
    hourly: 'Hourly', daily: 'Day Pass', 'half-day': 'Half Day', monthly: 'Monthly',
  }[booking.pricingType] || booking.pricingType;

  const isCard = booking.paymentMethod === 'card';
  const isBankDeposit = booking.paymentMethod === 'bank_deposit';
  const alreadyRefunded = localRefundStatus === 'refunded' || localRefundStatus === 'credit_note';
  const canRefund = localStatus === 'confirmed' && (isCard || isBankDeposit) && !alreadyRefunded;

  const handleStripeRefund = async (andCancel) => {
    if (!booking.stripePaymentIntentId) {
      toast.error('No Stripe payment ID on this booking.');
      return;
    }
    const label = andCancel ? 'Refund & Cancel' : 'Refund';
    if (!window.confirm(`${label}: $${booking.price} to ${booking.firstName || booking.email}? This cannot be undone.`)) return;
    setProcessing(true);
    try {
      const fns = getFunctions(undefined, 'australia-southeast1');
      const processRefund = httpsCallable(fns, 'processRefund');
      const result = await processRefund({ bookingId: booking.id, stripePaymentIntentId: booking.stripePaymentIntentId });
      await markRefunded(booking.id, result.data?.refundId);
      if (andCancel) {
        await cancelBooking(booking.id);
        setLocalStatus('cancelled');
      }
      setLocalRefundStatus('refunded');
      if (booking.email) {
        sendRefundConfirmation({
          to: booking.email,
          name: `${booking.firstName || ''} ${booking.lastName || ''}`.trim() || booking.email,
          spaceName: space?.name || booking.spaceId,
          startDate: start ? format(start, 'd MMM yyyy') : undefined,
          price: booking.price,
          refundType: 'stripe',
        }).catch(() => {});
      }
      toast.success(andCancel ? 'Refund processed and booking cancelled.' : 'Refund processed via Stripe.');
      setShowRefund(false);
      // Generate credit note if booking had an invoice
      if (booking.invoiceNumber) {
        generateCreditNote(booking.id).catch(() => {});
      }
    } catch (err) {
      toast.error(err.message || 'Refund failed.');
    }
    setProcessing(false);
  };

  const handleSaveCreditNote = async (andCancel) => {
    if (!creditNoteRef.trim()) { toast.error('Enter a credit note reference.'); return; }
    setProcessing(true);
    try {
      await addCreditNoteRef(booking.id, creditNoteRef.trim());
      if (andCancel) {
        await cancelBooking(booking.id);
        setLocalStatus('cancelled');
      }
      setLocalRefundStatus('credit_note');
      if (booking.email) {
        sendRefundConfirmation({
          to: booking.email,
          name: `${booking.firstName || ''} ${booking.lastName || ''}`.trim() || booking.email,
          spaceName: space?.name || booking.spaceId,
          startDate: start ? format(start, 'd MMM yyyy') : undefined,
          price: booking.price,
          refundType: 'credit_note',
          creditNoteRef: creditNoteRef.trim(),
        }).catch(() => {});
      }
      toast.success(andCancel ? 'Credit note saved and booking cancelled.' : 'Credit note reference saved.');
      setShowRefund(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save.');
    }
    setProcessing(false);
  };

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-semibold text-slate-800">
              <Link to={`/admin/booking/${booking.id}`} className="hover:text-brand-500 hover:underline">
                {`${booking.firstName || ''} ${booking.lastName || ''}`.trim() || booking.email || 'Unknown'}
              </Link>
            </h3>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
              {localStatus}
            </span>
            {booking.paymentMethod && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">
                {booking.paymentMethod === 'bank_deposit' ? 'Bank Deposit' : booking.paymentMethod === 'credits' ? 'Credits' : 'Card'}
              </span>
            )}
            {alreadyRefunded && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">
                {booking.refundStatus === 'credit_note' ? 'Credit Note' : 'Refunded'}
              </span>
            )}
            {booking.accessCodeStatus && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${{
                pending: 'bg-yellow-100 text-yellow-700',
                provisioned: 'bg-blue-100 text-blue-700',
                active: 'bg-green-100 text-green-700',
                expired: 'bg-gray-100 text-gray-500',
                revoked: 'bg-red-100 text-red-600',
              }[booking.accessCodeStatus] || 'bg-gray-100 text-gray-500'}`}>
                {booking.accessCodeStatus === 'pending' ? 'Access Pending' :
                 booking.accessCodeStatus === 'provisioned' ? 'Access Provisioned' :
                 booking.accessCodeStatus === 'active' ? 'Access Active' :
                 booking.accessCodeStatus === 'expired' ? 'Access Expired' :
                 booking.accessCodeStatus === 'revoked' ? 'Access Revoked' :
                 booking.accessCodeStatus}
              </span>
            )}
          </div>
          {booking.email && <p className="text-xs text-slate-400 mb-1">{booking.email} <span className="text-slate-300 font-mono ml-1">{booking.id?.slice(0, 8)}</span></p>}
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-slate-500">
            <span className="font-medium" style={{ color: space?.color }}>{space?.name || booking.spaceId}</span>
            <span>{pricingLabel}</span>
            {booking.seatName && <span className="text-slate-400">{booking.seatName}</span>}
          </div>
          <div className="mt-1 text-xs text-slate-400 flex flex-wrap gap-x-3">
            {start && end && (
              <span>
                {booking.pricingType === 'hourly'
                  ? `${format(start, 'EEE d MMM, h:mm a')} – ${format(end, 'h:mm a')}`
                  : `${format(start, 'd MMM yyyy')} → ${format(addDays(end, -1), 'd MMM yyyy')}`}
              </span>
            )}
            {created && <span>Booked {format(created, 'd MMM yyyy')}</span>}
          </div>
          {booking.stripePaymentIntentId && (
            <p className="mt-1 text-xs text-slate-400">
              Stripe:{' '}
              <a
                href={`https://dashboard.stripe.com/payments/${booking.stripePaymentIntentId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono hover:underline text-brand-500"
              >{booking.stripePaymentIntentId}</a>
            </p>
          )}
          {booking.creditNoteRef && (
            <p className="mt-1 text-xs text-slate-500">Credit note: <span className="font-mono">{booking.creditNoteRef}</span></p>
          )}
          {booking.accessPin && (
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-xs text-slate-400">
                PIN: <span className="font-mono font-bold tracking-widest text-slate-600">{booking.accessPin}</span>
              </span>
              <button
                onClick={handleSendPin}
                disabled={sendingPin}
                className="text-xs text-brand-500 hover:text-brand-700 underline disabled:opacity-50"
              >
                {sendingPin ? 'Sending…' : 'Send PIN'}
              </button>
            </div>
          )}
          {/* Invoice */}
          {booking.paymentMethod === 'card' && (
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              {booking.invoiceNumber ? (
                <>
                  <span className="text-xs text-slate-400">
                    Invoice: <a href={booking.invoiceUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-brand-500 hover:underline">{booking.invoiceNumber}</a>
                  </span>
                  <button
                    onClick={async () => {
                      try { await generateInvoice(booking.id, { resend: true }); toast.success(`Invoice ${booking.invoiceNumber} resent.`); }
                      catch { toast.error('Failed to resend invoice.'); }
                    }}
                    className="text-xs text-brand-500 hover:text-brand-700 underline"
                  >Resend</button>
                </>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      const res = await generateInvoice(booking.id);
                      toast.success(`Invoice ${res.invoiceNumber} generated and sent.`);
                    } catch { toast.error('Failed to generate invoice.'); }
                  }}
                  className="text-xs text-brand-500 hover:text-brand-700 underline"
                >Generate Invoice</button>
              )}
            </div>
          )}
        </div>
        <div className="text-right flex-shrink-0 space-y-1">
          <p className="text-lg font-bold text-slate-800">
            {booking.price === 0 ? 'Free' : `$${booking.price}`}
          </p>
          {booking.creditsApplied > 0 && (
            <p className="text-xs text-emerald-600">{booking.creditsApplied} hr credits</p>
          )}
          {canRefund && !showRefund && (
            <button
              onClick={() => setShowRefund(true)}
              className="text-xs text-red-500 hover:text-red-700 underline block ml-auto"
            >
              Refund
            </button>
          )}
        </div>
      </div>

      {showRefund && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          {isCard ? (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">
                Issue a <strong>${booking.price}</strong> Stripe refund
                {booking.stripePaymentIntentId ? (
                  <> — <a
                    href={`https://dashboard.stripe.com/payments/${booking.stripePaymentIntentId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-xs text-brand-600 hover:underline"
                  >{booking.stripePaymentIntentId}</a></>
                ) : ' — no payment ID found'}.
              </p>
              <div className="flex items-center justify-end gap-2 flex-wrap">
                <button onClick={() => setShowRefund(false)} className="text-xs text-slate-400 hover:text-slate-600">
                  Cancel
                </button>
                <button
                  onClick={() => handleStripeRefund(false)}
                  disabled={processing || !booking.stripePaymentIntentId}
                  className="btn-secondary text-xs disabled:opacity-50"
                >
                  {processing ? 'Processing…' : 'Refund Only'}
                </button>
                <button
                  onClick={() => handleStripeRefund(true)}
                  disabled={processing || !booking.stripePaymentIntentId}
                  className="btn-primary text-xs bg-red-500 hover:bg-red-600 disabled:opacity-50"
                >
                  {processing ? 'Processing…' : 'Refund & Cancel'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Enter the credit note reference number for this bank deposit refund:</p>
              <div className="flex items-center gap-2">
                <input
                  className="input-field py-1.5 text-sm flex-1"
                  placeholder="e.g. CN-2024-001"
                  value={creditNoteRef}
                  onChange={(e) => setCreditNoteRef(e.target.value)}
                />
                <button
                  onClick={() => setShowRefund(false)}
                  className="text-xs text-slate-400 hover:text-slate-600 flex-shrink-0"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveCreditNote(false)}
                  disabled={processing}
                  className="btn-secondary text-xs flex-shrink-0 disabled:opacity-50"
                >
                  {processing ? 'Saving…' : 'Save Only'}
                </button>
                <button
                  onClick={() => handleSaveCreditNote(true)}
                  disabled={processing}
                  className="btn-primary text-xs bg-red-500 hover:bg-red-600 flex-shrink-0 disabled:opacity-50"
                >
                  {processing ? 'Saving…' : 'Save & Cancel'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Promotions Manager ──────────────────────────────────────────────────────

const PromotionsManager = ({ spaces, promotions, setPromotions }) => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const twoWeeks = format(addDays(new Date(), 14), 'yyyy-MM-dd');
  const [form, setForm] = useState({
    name: '',
    spaceId: 'all',
    pricingType: 'all',
    discountType: 'percent',
    discountValue: '',
    purchaseStart: today,
    purchaseEnd: twoWeeks,
    applicableDates: [{ start: today, end: twoWeeks }],
    active: true,
  });
  const [saving, setSaving] = useState(false);

  const addDateRange = () => {
    setForm((f) => ({ ...f, applicableDates: [...f.applicableDates, { start: today, end: twoWeeks }] }));
  };
  const removeDateRange = (i) => {
    setForm((f) => ({ ...f, applicableDates: f.applicableDates.filter((_, j) => j !== i) }));
  };
  const updateDateRange = (i, key, val) => {
    setForm((f) => ({ ...f, applicableDates: f.applicableDates.map((d, j) => j === i ? { ...d, [key]: val } : d) }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name || !form.discountValue || !form.purchaseStart || !form.purchaseEnd) {
      toast.error('Fill in all required fields.'); return;
    }
    if (form.purchaseEnd < form.purchaseStart) { toast.error('Purchase end date must be after start.'); return; }
    const validDates = form.applicableDates.filter((d) => d.start && d.end && d.end >= d.start);
    if (validDates.length === 0) { toast.error('Add at least one valid date range for the discount.'); return; }
    setSaving(true);
    try {
      const promo = {
        name: form.name,
        spaceId: form.spaceId,
        pricingType: form.pricingType,
        discountType: form.discountType,
        discountValue: parseFloat(form.discountValue),
        purchaseStart: form.purchaseStart,
        purchaseEnd: form.purchaseEnd,
        applicableDates: validDates,
        // Legacy compat — set startDate/endDate to purchase window
        startDate: form.purchaseStart,
        endDate: form.purchaseEnd,
        active: true,
        createdAt: Timestamp.now(),
      };
      const ref = await addDoc(collection(db, 'promotions'), promo);
      setPromotions((prev) => [...prev, { id: ref.id, ...promo }]);
      setForm({ name: '', spaceId: 'all', pricingType: 'all', discountType: 'percent', discountValue: '', purchaseStart: today, purchaseEnd: twoWeeks, applicableDates: [{ start: today, end: twoWeeks }], active: true });
      toast.success('Promotion created.');
    } catch { toast.error('Failed to create promotion.'); }
    setSaving(false);
  };

  const handleToggle = async (promo) => {
    const next = !promo.active;
    try {
      await updateDoc(doc(db, 'promotions', promo.id), { active: next });
      setPromotions((prev) => prev.map((p) => p.id === promo.id ? { ...p, active: next } : p));
      toast.success(next ? 'Promotion enabled.' : 'Promotion paused.');
    } catch { toast.error('Failed to update.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      await deleteDoc(doc(db, 'promotions', id));
      setPromotions((prev) => prev.filter((p) => p.id !== id));
      toast.success('Promotion deleted.');
    } catch { toast.error('Failed to delete.'); }
  };

  const pricingTypes = [
    { id: 'all', label: 'All pricing types' },
    { id: 'daily', label: 'Daily' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'hourly', label: 'Hourly' },
  ];

  return (
    <div className="space-y-4">
      {/* Existing promotions */}
      {promotions.length > 0 && (
        <div className="card divide-y divide-gray-100">
          {promotions.map((promo) => {
            const space = promo.spaceId === 'all' ? null : spaces.find((s) => s.id === promo.spaceId);
            const purchaseEnd = promo.purchaseEnd || promo.endDate;
            const isExpired = purchaseEnd < today;
            const dates = promo.applicableDates || [{ start: promo.startDate, end: promo.endDate }];
            return (
              <div key={promo.id} className={`px-5 py-3 ${isExpired ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-700">{promo.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        isExpired ? 'bg-gray-100 text-gray-400' :
                        promo.active ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {isExpired ? 'Expired' : promo.active ? 'Active' : 'Paused'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {promo.discountType === 'percent' ? `${promo.discountValue}% off` : `$${promo.discountValue} off`}
                      {' · '}{space ? space.name : 'All spaces'}
                      {promo.pricingType !== 'all' && ` · ${promo.pricingType}`}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Purchase: {promo.purchaseStart || promo.startDate} → {purchaseEnd}
                    </p>
                    <p className="text-xs text-slate-400">
                      Applies to: {dates.map((d, i) => `${d.start} → ${d.end}`).join(', ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!isExpired && (
                      <button onClick={() => handleToggle(promo)} className="text-xs text-slate-400 hover:text-slate-600 underline">
                        {promo.active ? 'Pause' : 'Enable'}
                      </button>
                    )}
                    <button onClick={() => handleDelete(promo.id)} className="text-xs text-red-400 hover:text-red-600 underline">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add form */}
      <form onSubmit={handleAdd} className="card p-5 space-y-4">
        <h3 className="font-semibold text-slate-700 text-sm">Create Promotion</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="label text-xs">Promotion Name *</label>
            <input className="input-field py-1.5 text-sm" placeholder="e.g. Winter Hot Desk Special"
              value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          </div>
          <div>
            <label className="label text-xs">Applies to Space</label>
            <select className="input-field py-1.5 text-sm" value={form.spaceId}
              onChange={(e) => setForm((f) => ({ ...f, spaceId: e.target.value }))}>
              <option value="all">All spaces</option>
              {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label text-xs">Pricing Type</label>
            <select className="input-field py-1.5 text-sm" value={form.pricingType}
              onChange={(e) => setForm((f) => ({ ...f, pricingType: e.target.value }))}>
              {pricingTypes.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label text-xs">Discount Type</label>
            <select className="input-field py-1.5 text-sm" value={form.discountType}
              onChange={(e) => setForm((f) => ({ ...f, discountType: e.target.value }))}>
              <option value="percent">Percentage (%)</option>
              <option value="flat">Flat Amount ($)</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">Discount Value *</label>
            <input type="number" min="0" max={form.discountType === 'percent' ? '100' : undefined} step="any" className="input-field py-1.5 text-sm"
              placeholder={form.discountType === 'percent' ? 'e.g. 20' : 'e.g. 10'}
              value={form.discountValue} onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))} required />
          </div>
        </div>

        {/* Purchase window */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Purchase Window</p>
          <p className="text-xs text-slate-400 mb-2">When customers can see and purchase this promo.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label text-xs">From *</label>
              <input type="date" className="input-field py-1.5 text-sm" value={form.purchaseStart}
                onChange={(e) => setForm((f) => ({ ...f, purchaseStart: e.target.value }))} required />
            </div>
            <div>
              <label className="label text-xs">Until *</label>
              <input type="date" className="input-field py-1.5 text-sm" min={form.purchaseStart} value={form.purchaseEnd}
                onChange={(e) => setForm((f) => ({ ...f, purchaseEnd: e.target.value }))} required />
            </div>
          </div>
        </div>

        {/* Applicable booking dates */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Applicable Booking Dates</p>
              <p className="text-xs text-slate-400">Which booking dates the discount applies to.</p>
            </div>
            <button type="button" onClick={addDateRange} className="text-xs text-brand-500 hover:text-brand-700 font-medium">+ Add Range</button>
          </div>
          <div className="space-y-2">
            {form.applicableDates.map((range, i) => (
              <div key={i} className="grid grid-cols-5 gap-2 items-end">
                <div className="col-span-2">
                  <label className="label text-xs">From</label>
                  <input type="date" className="input-field py-1.5 text-sm" value={range.start}
                    onChange={(e) => updateDateRange(i, 'start', e.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="label text-xs">To</label>
                  <input type="date" className="input-field py-1.5 text-sm" min={range.start} value={range.end}
                    onChange={(e) => updateDateRange(i, 'end', e.target.value)} />
                </div>
                {form.applicableDates.length > 1 && (
                  <button type="button" onClick={() => removeDateRange(i)} className="text-red-400 hover:text-red-600 pb-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary text-sm disabled:opacity-50">
            {saving ? 'Creating…' : 'Create Promotion'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Create Member Form ───────────────────────────────────────────────────────

const TEMP_PASSWORD = () => Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-6).toUpperCase() + '!1';

const CreateMemberForm = ({ onCreated, onCancel, defaultWifiProfileId }) => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', company: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.firstName) { toast.error('First name and email are required.'); return; }
    setSaving(true);
    try {
      const tempPass = TEMP_PASSWORD();
      const cred = await createUserWithEmailAndPassword(auth, form.email, tempPass);
      const displayName = `${form.firstName} ${form.lastName}`.trim();
      await updateProfile(cred.user, { displayName });
      await setDoc(doc(db, 'users', cred.user.uid), {
        firstName: form.firstName,
        lastName: form.lastName,
        displayName,
        email: form.email,
        phone: normalisePhone(form.phone),
        company: form.company || '',
        role: 'member',
        createdAt: Timestamp.now(),
        ...(defaultWifiProfileId ? { wifiProfileId: defaultWifiProfileId } : {}),
      });
      await sendPasswordResetEmail(auth, form.email);
      toast.success(`Member created — password setup email sent to ${form.email}`);
      onCreated({ id: cred.user.uid, ...form, role: 'member' });
    } catch (err) {
      toast.error(err.message || 'Failed to create member.');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="card p-5 space-y-4 ring-2 ring-brand-300">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Create New Member</h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <p className="text-sm text-slate-500">An email will be sent to the member with a link to set their password.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label text-xs">First Name *</label>
          <input className="input-field py-1.5 text-sm" value={form.firstName} required
            onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} />
        </div>
        <div>
          <label className="label text-xs">Last Name</label>
          <input className="input-field py-1.5 text-sm" value={form.lastName}
            onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
        </div>
        <div className="sm:col-span-2">
          <label className="label text-xs">Email *</label>
          <input type="email" className="input-field py-1.5 text-sm" value={form.email} required
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        </div>
        <div>
          <label className="label text-xs">Phone <span className="text-slate-400 font-normal">(optional)</span></label>
          <input className="input-field py-1.5 text-sm" value={form.phone} placeholder="04XX XXX XXX or +61 4XX XXX XXX"
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          <p className="text-xs text-slate-400 mt-0.5">Used for SMS booking updates.</p>
        </div>
        <div>
          <label className="label text-xs">Company</label>
          <input className="input-field py-1.5 text-sm" value={form.company}
            onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="btn-secondary text-sm">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary text-sm disabled:opacity-50">
          {saving ? 'Creating…' : 'Create & Send Invite'}
        </button>
      </div>
    </form>
  );
};

// ─── Member Card ─────────────────────────────────────────────────────────────

const MemberCard = ({ user, wifiProfiles, onSave, onWifiChange, onRoleChange, onDelete, currentUserEmail }) => {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    phone: user.phone || '',
    company: user.company || '',
  });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [togglingAdmin, setTogglingAdmin] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
  const assignedProfile = wifiProfiles.find((p) => p.id === user.wifiProfileId);

  const handleSave = async () => {
    setSaving(true);
    const saveData = { ...form, phone: normalisePhone(form.phone) };
    try {
      await updateUserProfile(user.id, saveData);
      onSave(user.id, saveData);
      toast.success('Member updated.');
      setExpanded(false);
    } catch {
      toast.error('Failed to save.');
    }
    setSaving(false);
  };

  const handleResetPassword = async () => {
    if (!user.email) return;
    setResetting(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      toast.success(`Password reset email sent to ${user.email}`);
    } catch (err) {
      toast.error(err.message || 'Failed to send reset email.');
    }
    setResetting(false);
  };

  const handleToggleAdmin = async () => {
    const newRole = user.role === 'admin' ? 'member' : 'admin';
    setTogglingAdmin(true);
    try {
      await updateUserRole(user.id, newRole);
      onRoleChange(user.id, newRole);
      toast.success(newRole === 'admin' ? `${displayName} is now an admin.` : `${displayName} is no longer an admin.`);
    } catch {
      toast.error('Failed to update role.');
    }
    setTogglingAdmin(false);
  };

  return (
    <div className="card overflow-hidden">
      {/* Collapsed row */}
      <div className="flex items-center gap-4 px-5 py-4">
        <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-brand-600">
            {(user.firstName?.[0] || user.email?.[0] || '?').toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800">{displayName}</p>
          <p className="text-xs text-slate-400">{user.email}</p>
          {user.company && <p className="text-xs text-slate-400">{user.company}</p>}
        </div>
        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
          {user.role === 'admin' && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">Admin</span>
          )}
          {assignedProfile ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">{assignedProfile.name || assignedProfile.ssid}</span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">No WiFi</span>
          )}
        </div>
        <Link to={`/admin/member/${user.id}`} className="text-sm text-brand-500 hover:text-brand-700 font-medium flex-shrink-0 ml-2">
          Bookings
        </Link>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-sm text-brand-500 hover:text-brand-700 font-medium flex-shrink-0 ml-2"
        >
          {expanded ? 'Close' : 'Edit'}
        </button>
      </div>

      {/* Expanded edit form */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label text-xs">First Name</label>
              <input className="input-field py-1.5 text-sm" value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} />
            </div>
            <div>
              <label className="label text-xs">Last Name</label>
              <input className="input-field py-1.5 text-sm" value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
            </div>
            <div>
              <label className="label text-xs">Phone <span className="text-slate-400 font-normal">(optional)</span></label>
              <input className="input-field py-1.5 text-sm" value={form.phone} placeholder="04XX XXX XXX or +61 4XX XXX XXX"
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              <p className="text-xs text-slate-400 mt-0.5">Used for SMS booking updates.</p>
            </div>
            <div>
              <label className="label text-xs">Company</label>
              <input className="input-field py-1.5 text-sm" value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="label text-xs">Email (read-only)</label>
            <input className="input-field py-1.5 text-sm bg-gray-100 cursor-not-allowed" value={user.email || ''} readOnly />
          </div>
          {wifiProfiles.length > 0 && (
            <div>
              <label className="label text-xs">WiFi Profile</label>
              <select
                className="input-field py-1.5 text-sm"
                value={user.wifiProfileId || ''}
                onChange={async (e) => {
                  const val = e.target.value || null;
                  try {
                    await updateUserWifiProfile(user.id, val);
                    onWifiChange(user.id, val);
                    toast.success('WiFi profile updated.');
                  } catch { toast.error('Failed to update WiFi.'); }
                }}
              >
                <option value="">— No profile —</option>
                {wifiProfiles.map((p) => (
                  <option key={p.id} value={p.id}>{p.name || p.ssid}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <button
                onClick={handleResetPassword}
                disabled={resetting}
                className="text-sm text-slate-500 hover:text-slate-700 underline disabled:opacity-50"
              >
                {resetting ? 'Sending…' : 'Send password reset email'}
              </button>
              {user.email !== currentUserEmail && (
                <button
                  onClick={handleToggleAdmin}
                  disabled={togglingAdmin}
                  className={`text-sm underline disabled:opacity-50 ${user.role === 'admin' ? 'text-purple-500 hover:text-purple-700' : 'text-slate-400 hover:text-purple-600'}`}
                >
                  {togglingAdmin ? 'Updating…' : user.role === 'admin' ? 'Remove admin' : 'Make admin'}
                </button>
              )}
              {user.email !== currentUserEmail && (
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete ${displayName}? This will cancel their active bookings and remove their account. This cannot be undone.`)) return;
                    setDeleting(true);
                    try {
                      await onDelete(user.id, user.email);
                      toast.success(`${displayName} deleted.`);
                    } catch { toast.error('Failed to delete member.'); }
                    setDeleting(false);
                  }}
                  disabled={deleting}
                  className="text-sm text-red-400 hover:text-red-600 underline disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Delete member'}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setExpanded(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-primary text-sm disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

// ─── AdminPage ────────────────────────────────────────────────────────────────

const ADMIN_EMAILS = [
  process.env.REACT_APP_ADMIN_EMAIL,
  'jack@thehubcrookwell.com.au',
].filter(Boolean);

export default function AdminPage() {
  const { currentUser, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('bookings');
  const [spaces, setSpaces] = useState(SPACES);
  const [blocks, setBlocks] = useState([]);
  const [bookingsData, setBookingsData] = useState([]);
  const [creditsData, setCreditsData] = useState([]);
  const [bankDetails, setBankDetails] = useState({ accountName: 'The Hub Crookwell', bsb: '', accountNumber: '' });
  const [wifiProfiles, setWifiProfiles] = useState([]);
  const [defaultWifiProfileId, setDefaultWifiProfileId] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [footerConfig, setFooterConfig] = useState(DEFAULT_FOOTER);
  const [accessCodesEnabled, setAccessCodesEnabled] = useState(false);
  const [bookingsEnabled, setBookingsEnabled] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateMember, setShowCreateMember] = useState(false);
  const [showAdminBooking, setShowAdminBooking] = useState(false);

  const isAdmin = currentUser && (ADMIN_EMAILS.includes(currentUser.email) || userProfile?.role === 'admin');

  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      const [cfg, blks, bkgs, creds, bank, wifiProfs, users, footer, accessEnabled, promos] = await Promise.all([
        loadSpaceConfig(SPACES),
        loadManualBlocks(),
        getAllBookings().catch(() => []),
        getAllMemberCredits().catch(() => []),
        loadBankDetails(),
        loadWifiProfiles(),
        getAllUsers().catch(() => []),
        loadFooterConfig(),
        getAccessFeatureEnabled().catch(() => false),
        getDocs(collection(db, 'promotions')).then((s) => s.docs.map((d) => ({ id: d.id, ...d.data() }))).catch(() => []),
      ]);
      setSpaces(cfg);
      setBlocks(blks);
      setBookingsData(bkgs);
      setCreditsData(creds);
      setBankDetails(bank);
      setWifiProfiles(wifiProfs.profiles);
      setDefaultWifiProfileId(wifiProfs.defaultProfileId);
      setUsersData(users);
      setFooterConfig(footer);
      setAccessCodesEnabled(accessEnabled ?? false);
      setPromotions(promos);
      // Load bookings enabled flag from features doc
      try {
        const featSnap = await getDoc(doc(db, 'config', 'features'));
        if (featSnap.exists()) setBookingsEnabled(featSnap.data().bookingsEnabled ?? false);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="card p-8 text-center max-w-sm w-full">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m2-11a7 7 0 110 14 7 7 0 010-14z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-sm text-slate-500 mb-4">This page is restricted to administrators.</p>
          <Link to="/" className="btn-secondary text-sm">Back to Home</Link>
        </div>
      </div>
    );
  }

  const handleSpaceSave = async (updatedSpace) => {
    const updated = spaces.map((s) => s.id === updatedSpace.id ? updatedSpace : s);
    setSpaces(updated);
    setSaving(true);
    await saveSpaceConfig(updated);
    setSaving(false);
  };

  const handleAddBlock = async (form) => {
    const block = {
      spaceId: form.spaceId,
      seatName: form.seatName || null,
      startDate: form.startDate,
      endDate: form.endDate,
      memberName: form.memberName,
      note: form.note,
      userEmail: form.userEmail || null,
      type: 'manual',
    };
    const id = await saveManualBlock(block);
    setBlocks((prev) => [...prev, { ...block, id }]);
    toast.success('Dates blocked successfully.');
  };

  const handleDeleteBlock = async (id) => {
    if (!window.confirm('Remove this block?')) return;
    await deleteManualBlock(id);
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    toast.success('Block removed.');
  };

  const generatePaymentSchedule = (booking) => {
    const start = booking.startDate?.toDate ? booking.startDate.toDate() : new Date(booking.startDate);
    const end = booking.endDate?.toDate ? booking.endDate.toDate() : new Date(booking.endDate);
    const existing = booking.paymentSchedule || [];
    if (booking.pricingType === 'monthly') {
      const numMonths = Math.max(1, differenceInCalendarMonths(end, start));
      const unitAmount = booking.unitPrice || (numMonths > 0 ? Math.round(booking.price / numMonths) : booking.price);
      return Array.from({ length: numMonths }, (_, i) => {
        const dueDate = addMonths(start, i);
        const id = format(dueDate, 'yyyy-MM');
        return existing.find((e) => e.id === id) || { id, dueDate: format(dueDate, 'yyyy-MM-dd'), label: format(dueDate, 'MMMM yyyy'), amount: unitAmount, paid: false, paidAt: null };
      });
    }
    return [existing.find((e) => e.id === 'single') || {
      id: 'single',
      dueDate: format(start, 'yyyy-MM-dd'),
      label: `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`,
      amount: booking.price,
      paid: false,
      paidAt: null,
    }];
  };

  const handleTogglePayment = async (booking, payment, paidData) => {
    const schedule = generatePaymentSchedule(booking);
    // paidData present = mark paid with date/invoice; absent = mark unpaid
    const nowPaid = paidData !== undefined;
    const updated = schedule.map((p) =>
      p.id === payment.id ? {
        ...p,
        paid: nowPaid,
        paidAt: nowPaid ? new Date().toISOString() : null,
        paidDate: nowPaid ? (paidData?.paidDate || null) : null,
        xeroInvoiceNumber: nowPaid ? (paidData?.xeroInvoiceNumber || null) : null,
      } : p
    );
    try {
      await updatePaymentSchedule(booking.id, updated);
      setBookingsData((prev) => prev.map((b) => b.id === booking.id ? { ...b, paymentSchedule: updated } : b));
      toast.success(nowPaid ? 'Marked as paid.' : 'Marked as unpaid.');
    } catch {
      toast.error('Failed to update payment.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const adminCurrentMonth = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();

  const handleTopUpCredits = async (userId, month, hours) => {
    await topUpMemberCredits(userId, month, hours);
    setCreditsData((prev) => prev.map((m) => {
      if (m.id !== userId) return m;
      const existing = m.credits?.[month] || { total: 0, used: 0 };
      return { ...m, credits: { ...(m.credits || {}), [month]: { ...existing, total: existing.total + hours } } };
    }));
    toast.success(`Added ${hours} hrs to credit balance.`);
  };

  const tabs = [
    { id: 'bookings', label: 'Bookings' },
    { id: 'members', label: 'Members' },
    { id: 'spaces', label: 'Manage Spaces' },
    { id: 'blocks', label: 'Manual Blocks' },
    { id: 'payments', label: 'Payments' },
    { id: 'credits', label: 'Credits' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Admin</span>
              </div>
              <h1 className="text-2xl font-extrabold">The Hub Dashboard</h1>
              <p className="text-slate-400 text-sm mt-1">{currentUser?.email}</p>
            </div>
            <div className="flex gap-3">
              <Link to="/availability" className="text-sm text-slate-300 hover:text-white transition-colors">
                View Calendar →
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-6 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand-400 text-white bg-slate-800'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Bookings tab */}
        {activeTab === 'bookings' && (() => {
          const now = new Date();
          const toD = (v) => v?.toDate ? v.toDate() : (v ? new Date(v) : null);
          const nonGuest = bookingsData.filter((b) => b.type !== 'guest');
          const pending = nonGuest.filter((b) => b.status === 'pending');
          const pendingIds = new Set(pending.map((b) => b.id));
          const current = nonGuest.filter((b) => {
            if (pendingIds.has(b.id)) return false;
            if (b.status !== 'confirmed') return false;
            const end = toD(b.endDate);
            return end && end >= now;
          });
          const activeBlocks = blocks.filter((bl) => new Date(bl.endDate) >= now);

          const Section = ({ title, description, items, emptyMsg }) => (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-1">
                {title} ({items.length})
              </h3>
              {description && <p className="text-xs text-slate-400 mb-3">{description}</p>}
              {items.length === 0 ? (
                <div className="card p-6 text-center text-sm text-slate-400">{emptyMsg}</div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => item._isBlock
                    ? (
                      <div key={item.id} className="card p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="font-semibold text-slate-800">{item.memberName || 'Manual Block'}</h3>
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700">Manual Block</span>
                              {item.userEmail && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{item.userEmail}</span>}
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-slate-500">
                              <span className="font-medium" style={{ color: spaces.find((s) => s.id === item.spaceId)?.color }}>
                                {spaces.find((s) => s.id === item.spaceId)?.name || item.spaceId}
                              </span>
                              {item.seatName && <span className="text-slate-400">{item.seatName}</span>}
                            </div>
                            <div className="mt-1 text-xs text-slate-400">
                              {item.startDate} → {item.endDate}
                              {item.note && <span className="ml-2 italic">"{item.note}"</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                    : <BookingCard key={item.id} booking={item} spaces={spaces} />
                  )}
                </div>
              )}
            </div>
          );

          const activeItems = [
            ...current.map((b) => ({ ...b, _isBlock: false })),
            ...activeBlocks.map((bl) => ({ ...bl, _isBlock: true })),
          ].sort((a, b) => {
            const aD = a._isBlock ? new Date(a.startDate) : (toD(a.startDate) || 0);
            const bD = b._isBlock ? new Date(b.startDate) : (toD(b.startDate) || 0);
            return aD - bD;
          });

          return (
            <div className="space-y-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Current & Pending Bookings</h2>
                  <p className="text-sm text-slate-500">Active confirmed bookings, manual blocks, and bookings awaiting confirmation.</p>
                </div>
                {!showAdminBooking && (
                  <button onClick={() => setShowAdminBooking(true)} className="btn-primary text-sm flex-shrink-0">
                    + Book on Behalf
                  </button>
                )}
              </div>
              {showAdminBooking && (
                <AdminBookingForm
                  spaces={spaces}
                  users={usersData}
                  bankDetails={bankDetails}
                  onCreated={() => {
                    setShowAdminBooking(false);
                    getAllBookings().catch(() => []).then(setBookingsData);
                  }}
                  onCancel={() => setShowAdminBooking(false)}
                />
              )}
              <Section
                title="Pending"
                description="Bookings awaiting payment confirmation (bank deposit) or admin approval."
                items={pending.map((b) => ({ ...b, _isBlock: false }))}
                emptyMsg="No pending bookings."
              />
              <Section
                title="Active / Upcoming"
                description="Confirmed bookings that are currently active or scheduled for the future, plus manual blocks."
                items={activeItems}
                emptyMsg="No current or upcoming bookings."
              />
            </div>
          );
        })()}

        {/* Members tab */}
        {activeTab === 'members' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Members</h2>
                <p className="text-sm text-slate-500">
                  Edit member details, assign WiFi profiles, and send password resets.
                </p>
              </div>
              {!showCreateMember && (
                <button onClick={() => setShowCreateMember(true)} className="btn-primary text-sm">
                  + Create Member
                </button>
              )}
            </div>
            {showCreateMember && (
              <CreateMemberForm
                onCreated={(newUser) => {
                  setUsersData((prev) => [...prev, newUser]);
                  setShowCreateMember(false);
                }}
                onCancel={() => setShowCreateMember(false)}
                defaultWifiProfileId={defaultWifiProfileId}
              />
            )}
            {usersData.length === 0 ? (
              <div className="card p-8 text-center text-sm text-slate-400">
                No members found. They'll appear here once users register.
              </div>
            ) : (
              <div className="space-y-3">
                {[...usersData]
                  .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
                  .map((user) => (
                    <MemberCard
                      key={user.id}
                      user={user}
                      wifiProfiles={wifiProfiles}
                      onSave={(uid, data) => setUsersData((prev) => prev.map((u) => u.id === uid ? { ...u, ...data } : u))}
                      onWifiChange={(uid, val) => setUsersData((prev) => prev.map((u) => u.id === uid ? { ...u, wifiProfileId: val } : u))}
                      onRoleChange={(uid, role) => setUsersData((prev) => prev.map((u) => u.id === uid ? { ...u, role } : u))}
                      onDelete={async (uid) => {
                        // Cancel active bookings
                        const userBookings = bookingsData.filter((b) => b.userId === uid && b.status === 'confirmed');
                        for (const b of userBookings) {
                          await cancelBooking(b.id).catch(() => {});
                        }
                        // Delete Firestore user doc
                        await deleteDoc(doc(db, 'users', uid));
                        // Delete Firebase Auth user via Cloud Function
                        const fns = getFunctions(undefined, 'australia-southeast1');
                        const deleteMember = httpsCallable(fns, 'deleteMember');
                        await deleteMember({ uid });
                        // Remove from UI
                        setUsersData((prev) => prev.filter((u) => u.id !== uid));
                        setBookingsData((prev) => prev.map((b) => b.userId === uid ? { ...b, status: 'cancelled' } : b));
                      }}
                      currentUserEmail={currentUser?.email}
                    />
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Spaces tab */}
        {activeTab === 'spaces' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Spaces & Pricing</h2>
                <p className="text-sm text-slate-500">Edit the name, capacity, and rates for each space. Changes are saved immediately.</p>
              </div>
              {saving && (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <span className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
                  Saving…
                </div>
              )}
            </div>

            {spaces.map((space) => (
              <SpaceEditor key={space.id} space={space} onSave={handleSpaceSave} />
            ))}

            <div className="card p-5 border-dashed border-2 border-gray-200 text-center">
              <p className="text-sm text-slate-400 mb-1">Need to add a new space type?</p>
              <p className="text-xs text-slate-300">Contact your developer to add new space IDs to the system. Existing spaces can be fully configured above.</p>
            </div>
          </div>
        )}

        {/* Manual Blocks tab */}
        {activeTab === 'blocks' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Manual Blocks</h2>
              <p className="text-sm text-slate-500">
                Use this section to block out dates for spaces that have been booked off-system (phone, email, direct agreement) or for maintenance/closure. Blocked dates will show as unavailable in the booking calendar.
              </p>
            </div>

            <ManualBlockForm spaces={spaces} users={usersData} onAdd={handleAddBlock} />

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">
                Active Blocks ({blocks.length})
              </h3>
              <BlockList blocks={blocks} spaces={spaces} users={usersData} onDelete={handleDeleteBlock} accessCodesEnabled={accessCodesEnabled} />
            </div>
          </div>
        )}

        {/* Credits tab */}
        {activeTab === 'credits' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Meeting Room Credits</h2>
              <p className="text-sm text-slate-500">
                Monthly members receive 20 hrs of meeting room credits per month (use it or lose it).
                Credits are auto-provisioned when a monthly membership is purchased or when a member first books the meeting room that month.
                Use "Top Up" to manually add hours to any member.
              </p>
            </div>

            {creditsData.length === 0 ? (
              <div className="card p-8 text-center text-sm text-slate-400">
                No credit records yet. Credits will appear here once members with monthly memberships book the meeting room.
              </div>
            ) : (
              <div className="space-y-4">
                {creditsData
                  .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''))
                  .map((member) => (
                    <MemberCreditCard
                      key={member.id}
                      member={member}
                      currentMonth={adminCurrentMonth}
                      onTopUp={handleTopUpCredits}
                      userRecord={usersData.find((u) => u.id === member.id || u.email === member.email)}
                    />
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Settings tab */}
        {activeTab === 'settings' && (
          <div className="space-y-8 max-w-lg">

            {/* Accept Bookings */}
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Bookings</h2>
                <p className="text-sm text-slate-500">Control whether the site accepts new bookings.</p>
              </div>
              <div className="card p-5 flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-slate-700">Accept new bookings</p>
                  <p className="text-sm text-slate-400 mt-0.5">
                    {bookingsEnabled ? 'Live — members can book spaces.' : 'Paused — booking page shows a "coming soon" message.'}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    const next = !bookingsEnabled;
                    try {
                      await setDoc(doc(db, 'config', 'features'), { bookingsEnabled: next }, { merge: true });
                      setBookingsEnabled(next);
                      toast.success(next ? 'Bookings enabled.' : 'Bookings paused.');
                    } catch { toast.error('Failed to update.'); }
                  }}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    bookingsEnabled ? 'bg-brand-500' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                    bookingsEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            </div>

            {/* Promotions */}
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Promotions</h2>
                <p className="text-sm text-slate-500">Create time-limited discounts that automatically apply during booking.</p>
              </div>

              <PromotionsManager spaces={spaces} promotions={promotions} setPromotions={setPromotions} />
            </div>

            {/* Door Access Codes */}
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Door Access Codes</h2>
                <p className="text-sm text-slate-500">
                  When enabled, members are automatically assigned a 4-digit PIN when they book.
                  The on-site Pi agent provisions the PIN in UniFi Access 1 hour before their booking starts and revokes it when it ends.
                </p>
              </div>
              <div className="card p-5 flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-slate-700">Enable access code provisioning</p>
                  <p className="text-sm text-slate-400 mt-0.5">
                    {accessCodesEnabled ? 'Active — PINs will be issued for new confirmed bookings.' : 'Inactive — no PINs will be issued.'}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    const next = !accessCodesEnabled;
                    try {
                      await setAccessFeatureEnabled(next);
                      setAccessCodesEnabled(next);
                      toast.success(next ? 'Access codes enabled.' : 'Access codes disabled.');
                    } catch {
                      toast.error('Failed to update setting.');
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    accessCodesEnabled ? 'bg-brand-500' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                    accessCodesEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            </div>


            {/* Daily Digest */}
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Daily Digest Email</h2>
                <p className="text-sm text-slate-500">Automated daily summary sent at 7am AEST to all admin accounts.</p>
              </div>
              <div className="card p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Send a test digest now to check formatting.</p>
                  <p className="text-xs text-slate-400 mt-0.5">Sends to: {process.env.REACT_APP_ADMIN_EMAIL || 'admin accounts'}</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      toast.loading('Sending digest…', { id: 'digest' });
                      const res = await fetch(
                        `${process.env.REACT_APP_FUNCTIONS_BASE_URL || 'https://australia-southeast1-the-hub-crookwell.cloudfunctions.net'}/sendAdminDigest`,
                        { method: 'POST' }
                      );
                      const data = await res.json();
                      if (data.ok) toast.success('Digest sent!', { id: 'digest' });
                      else toast.error(data.error || 'Failed', { id: 'digest' });
                    } catch { toast.error('Failed to send digest.', { id: 'digest' }); }
                  }}
                  className="btn-secondary text-sm flex-shrink-0"
                >
                  Send Test Digest
                </button>
              </div>
            </div>

            {/* Bank Details */}
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Bank Transfer Details</h2>
                <p className="text-sm text-slate-500">Shown in confirmation emails when a customer chooses bank deposit.</p>
              </div>
              <div className="card p-6 space-y-4">
                <div>
                  <label className="label">Account Name</label>
                  <input type="text" className="input-field" value={bankDetails.accountName}
                    onChange={(e) => setBankDetails((d) => ({ ...d, accountName: e.target.value }))} />
                </div>
                <div>
                  <label className="label">BSB</label>
                  <input type="text" className="input-field" placeholder="e.g. 062-000" value={bankDetails.bsb}
                    onChange={(e) => setBankDetails((d) => ({ ...d, bsb: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Account Number</label>
                  <input type="text" className="input-field" placeholder="e.g. 12345678" value={bankDetails.accountNumber}
                    onChange={(e) => setBankDetails((d) => ({ ...d, accountNumber: e.target.value }))} />
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    onClick={async () => {
                      setSaving(true);
                      try { await saveBankDetails(bankDetails); toast.success('Bank details saved.'); }
                      catch { toast.error('Failed to save.'); }
                      setSaving(false);
                    }}
                    disabled={saving}
                    className="btn-primary disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>

            {/* WiFi Profiles */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">WiFi Profiles</h2>
                  <p className="text-sm text-slate-500">Define WiFi networks. Assign to members in the Members tab.</p>
                </div>
                <button
                  onClick={() => {
                    const newProfile = { id: Date.now().toString(36), name: '', ssid: '', password: '' };
                    setWifiProfiles((p) => [...p, newProfile]);
                  }}
                  className="btn-primary text-sm px-4 py-2"
                >+ Add Profile</button>
              </div>
              {wifiProfiles.length === 0 ? (
                <div className="card p-5 text-center text-sm text-slate-400">No WiFi profiles yet. Click "Add Profile" to create one.</div>
              ) : (
                <div className="space-y-3">
                  {wifiProfiles.map((profile, i) => (
                    <div key={profile.id} className={`card p-4 space-y-3 ${defaultWifiProfileId === profile.id ? 'ring-2 ring-brand-400' : ''}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="label text-xs">Profile Name</label>
                          <input type="text" className="input-field py-1.5 text-sm" placeholder="e.g. Members WiFi"
                            value={profile.name}
                            onChange={(e) => setWifiProfiles((p) => p.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                        </div>
                        <div>
                          <label className="label text-xs">SSID</label>
                          <input type="text" className="input-field py-1.5 text-sm" placeholder="Network name"
                            value={profile.ssid}
                            onChange={(e) => setWifiProfiles((p) => p.map((x, j) => j === i ? { ...x, ssid: e.target.value } : x))} />
                        </div>
                        <div>
                          <label className="label text-xs">Password</label>
                          <input type="text" className="input-field py-1.5 text-sm" placeholder="Password"
                            value={profile.password}
                            onChange={(e) => setWifiProfiles((p) => p.map((x, j) => j === i ? { ...x, password: e.target.value } : x))} />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          {defaultWifiProfileId === profile.id ? (
                            <span className="text-xs font-medium text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full">Default</span>
                          ) : (
                            <button
                              onClick={() => setDefaultWifiProfileId(profile.id)}
                              className="text-xs text-slate-400 hover:text-brand-600"
                            >Set as Default</button>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setWifiProfiles((p) => p.filter((_, j) => j !== i));
                            if (defaultWifiProfileId === profile.id) setDefaultWifiProfileId(null);
                          }}
                          className="text-xs text-red-400 hover:text-red-600"
                        >Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={async () => {
                    setSaving(true);
                    try { await saveWifiProfiles(wifiProfiles, defaultWifiProfileId); toast.success('WiFi profiles saved.'); }
                    catch { toast.error('Failed to save.'); }
                    setSaving(false);
                  }}
                  disabled={saving}
                  className="btn-primary disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save WiFi Profiles'}
                </button>
              </div>
            </div>

            {/* Footer Content */}
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Footer Content</h2>
                <p className="text-sm text-slate-500">Contact, hours, and branding info shown in the site footer.</p>
              </div>
              <div className="card p-6 space-y-4">
                <div>
                  <label className="label">Brand Description</label>
                  <textarea rows={3} className="input-field" value={footerConfig.brandDescription}
                    onChange={(e) => setFooterConfig((f) => ({ ...f, brandDescription: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Address</label>
                  <input type="text" className="input-field" value={footerConfig.address}
                    onChange={(e) => setFooterConfig((f) => ({ ...f, address: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Contact Email</label>
                  <input type="email" className="input-field" value={footerConfig.email}
                    onChange={(e) => setFooterConfig((f) => ({ ...f, email: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Contact Phone <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input type="tel" className="input-field" placeholder="e.g. 02 1234 5678" value={footerConfig.phone || ''}
                    onChange={(e) => setFooterConfig((f) => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="label">Weekday Hours</label>
                    <input type="text" className="input-field" placeholder="e.g. Mon–Fri 8am–6pm" value={footerConfig.hoursWeekday}
                      onChange={(e) => setFooterConfig((f) => ({ ...f, hoursWeekday: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Saturday Hours</label>
                    <input type="text" className="input-field" placeholder="e.g. Sat 9am–1pm" value={footerConfig.hoursSaturday}
                      onChange={(e) => setFooterConfig((f) => ({ ...f, hoursSaturday: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Members Note</label>
                    <input type="text" className="input-field" placeholder="e.g. Members: 24/7 access" value={footerConfig.hoursMembersNote}
                      onChange={(e) => setFooterConfig((f) => ({ ...f, hoursMembersNote: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="label">Footer Tagline</label>
                  <input type="text" className="input-field" placeholder="e.g. Built for co-workers, by co-workers." value={footerConfig.tagline}
                    onChange={(e) => setFooterConfig((f) => ({ ...f, tagline: e.target.value }))} />
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    onClick={async () => {
                      setSaving(true);
                      try { await saveFooterConfig(footerConfig); toast.success('Footer saved.'); }
                      catch { toast.error('Failed to save.'); }
                      setSaving(false);
                    }}
                    disabled={saving}
                    className="btn-primary disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payments tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Bank Deposit Payments</h2>
              <p className="text-sm text-slate-500">Track payment status for bank deposit bookings. Monthly bookings are split by month.</p>
            </div>
            {(() => {
              const bankBookings = bookingsData.filter((b) => b.paymentMethod === 'bank_deposit' && b.type !== 'guest');
              if (bankBookings.length === 0) {
                return (
                  <div className="card p-8 text-center text-sm text-slate-400">
                    No bank deposit bookings found. They will appear here once customers choose bank deposit at checkout.
                  </div>
                );
              }
              return (
                <div className="space-y-4">
                  {bankBookings.map((booking) => {
                    const schedule = generatePaymentSchedule(booking);
                    const paidCount = schedule.filter((p) => p.paid).length;
                    const space = spaces.find((s) => s.id === booking.spaceId);
                    const startD = booking.startDate?.toDate ? booking.startDate.toDate() : new Date(booking.startDate);
                    return (
                      <div key={booking.id} className="card p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-slate-800">
                                {`${booking.firstName || ''} ${booking.lastName || ''}`.trim() || booking.email || booking.userId || 'Unknown'}
                              </h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                booking.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                                'bg-amber-100 text-amber-700'
                              }`}>{booking.status}</span>
                            </div>
                            {booking.email && <p className="text-xs text-slate-400">{booking.email}</p>}
                            <p className="text-sm text-slate-500">{space?.name || booking.spaceId} · {booking.pricingType}</p>
                            {booking.seatName && <p className="text-xs text-slate-400">{booking.seatName}</p>}
                            <p className="text-xs text-slate-400">{format(startD, 'd MMM yyyy')}</p>
                          </div>
                          <span className="text-xs font-medium text-slate-500">{paidCount}/{schedule.length} paid</span>
                        </div>
                        <div className="mt-1">
                          {schedule.map((payment) => (
                            <PaymentRow
                              key={payment.id}
                              booking={booking}
                              payment={payment}
                              onToggle={handleTogglePayment}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
